import { type SkyName } from "@common/utils/mapWeather";
import { Color, Container, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { CameraManager } from "../managers/cameraManager";
import { MapManager } from "../managers/mapManager";
import { Lighting } from "../managers/lighting";
import { type Lightning, RAYS } from "../managers/lightingLogic";
import { NIGHT_ZINDEX, NightLight } from "../managers/nightLight";
import { UIManager } from "../managers/uiManager";
import { t } from "../utils/translations/phrases";
import { WarAudio } from "./warAudio";
import { SNOW_PALETTE, WarLook } from "./warLook";

/*
 * The match's sky (1.05, owner decision, 28 October 2026: "add the new night, snow, thunderstoirm and dusk modes, also
 * can choose which in quick start (for our dev mode), in game, they're random 1/5% chance each will play"): the server
 * rolls it (a clear day, dusk, night, snow or a thunderstorm, one in five each) and the map packet says which.
 *
 *  - dusk: a low orange sun to one side, warm light and plum shadows, long shadows behind the players and the trees
 *    (0.98.6: every shadow is managers/shadowLook.ts's, along the sun managers/lighting.ts gives; its rays here)
 *  - night: dim under a moonlit blue, every player with a flashlight (managers/nightLight.ts)
 *  - snow: the ground white (the minimap too), snow falling, boot prints that stay, a cold light and the wind
 *  - a thunderstorm: dark and grey-blue, heavy slanting rain, lightning that lights the whole field and its thunder
 *
 * Its colours come from the battlefield look's grade where that's drawn (High and Medium: war/warLook.ts); elsewhere
 * (Low, Off, a browser without WebGL) a tint over the world gives them, so every graphics level has its sky. Night's
 * dark is the same everywhere: it's the game. The sounds (thunder, rain, wind) play at every level.
 */

/** the tints over the world where the grade isn't drawn (multiplied in: `alpha` its strength) */
const TINTS: Partial<Readonly<Record<SkyName, { readonly color: number, readonly alpha: number }>>> = {
    dusk: { color: 0xffc596, alpha: 0.9 },
    snow: { color: 0xe6eeff, alpha: 0.8 },
    storm: { color: 0x8d99ad, alpha: 1 }
};

/** the camera container's zIndex for the sky's tint, the sun's glow and the lightning: just under the night's dark */
export const SKY_ZINDEX = NIGHT_ZINDEX - 1;

/** a soft round glow, white (the sun's): made once */
let glowTexture: Texture | undefined;
const radial = (stops: ReadonlyArray<readonly [number, string]>): Texture => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 128;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
        for (const [at, color] of stops) g.addColorStop(at, color);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 128, 128);
    }
    return Texture.from(canvas);
};
const sunTexture = (): Texture => glowTexture ??= radial([[0, "rgba(255,255,255,1)"], [0.4, "rgba(255,255,255,0.45)"], [1, "rgba(255,255,255,0)"]]);
/**
 * the sun's rays at dusk (0.98.6, the lighting at High): streaks of light fanning out from the sun, made once (a
 * square of them, the sun at its middle, turned slowly over the sun's glow)
 */
let raysTexture: Texture | undefined;
const sunRaysTexture = (): Texture => {
    if (raysTexture) return raysTexture;
    const size = 512;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const image = ctx.createImageData(size, size);
        const data = image.data;
        const half = size / 2;
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                const dx = x + 0.5 - half, dy = y + 0.5 - half;
                const r = Math.hypot(dx, dy) / half;
                const a = Math.atan2(dy, dx);
                // (a few broad rays and many fine ones, each fading out along its length; none at the very middle)
                const broad = 0.5 + 0.5 * Math.sin(a * 7 + 0.4) * Math.sin(a * 3 - 1.1);
                const fine = 0.5 + 0.5 * Math.sin(a * 23 + 2.0) * Math.sin(a * 41 + 0.7);
                const rays = Math.max(0, broad * 0.7 + fine * 0.5 - 0.35);
                const along = Math.min(1, r * 2.5) * Math.max(0, 1 - r);
                const i = (y * size + x) * 4;
                data[i] = 255; data[i + 1] = 236; data[i + 2] = 200;
                data[i + 3] = Math.round(255 * Math.min(1, rays * along * 1.4));
            }
        }
        ctx.putImageData(image, 0, 0);
    }
    return raysTexture = Texture.from(canvas);
};
/** dusk's light across the screen, multiplied in: as it is by the low sun (lower left), dimmer and bluer away from it */
let duskTexture: Texture | undefined;
const duskLightTexture = (): Texture => {
    if (duskTexture) return duskTexture;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 256;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const g = ctx.createLinearGradient(0, 256, 256, 0);
        g.addColorStop(0, "rgb(255, 247, 238)");
        g.addColorStop(0.55, "rgb(222, 205, 210)");
        g.addColorStop(1, "rgb(156, 142, 172)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 256, 256);
    }
    return duskTexture = Texture.from(canvas);
};

/** a lightning strike: its flashes (when, ms after it starts; how bright; how long they fade), and its thunder's due */
interface Strike {
    readonly start: number
    readonly pulses: ReadonlyArray<{ readonly at: number, readonly peak: number, readonly fade: number }>
    /** which way it struck (radians on screen): the lighting's shadows fall away from it (0.98.6) */
    readonly from: number
}

class WarSkyClass {
    private _sky: SkyName = "clear";
    get sky(): SkyName { return this._sky; }

    private _layer?: Container;
    private _tint?: Sprite;
    private _dusk?: Sprite;
    private _sun?: Sprite;
    private _rays?: Sprite;
    private _flash?: Sprite;
    /** the map's colours as they were before the snow's (put back for the next match that isn't snowy) */
    private _stock?: Map<string, Color>;
    private _nextStrike = 0;
    private _strike?: Strike;

    /** the map packet came, under this sky: before its colours are chosen and its map is drawn */
    begin(sky: SkyName): void {
        this._sky = sky;
        this._strike = undefined;
        this._nextStrike = performance.now() + 4000 + Math.random() * 6000;
        NightLight.begin(sky === "night");
        // the sounds of the sky, as long as the match lasts
        WarAudio.setRain(sky === "storm" ? 1 : 0);
        WarAudio.setWind(sky === "snow" ? 1 : sky === "storm" ? 0.5 : 0);
        this._announce(sky);
    }

    private _announced?: number;

    /** the sky, said once as the match begins (low in the middle, where a pickup's words show) */
    private _announce(sky: SkyName): void {
        // (0.98.4: each sky's weather and creatures said with it)
        const words = sky === "dusk" ? t("Dusk: the sun is going down, a dust storm is rolling in, the Mothman is on the roofs")
            : sky === "night" ? t("Night: a flashlight each, and the wildfire closing in")
                : sky === "snow" ? t("Snow: your boot prints stay in it, and something big is out there")
                    : sky === "storm" ? t("Thunderstorm: tornadoes over the north, the river rising in the south")
                        : undefined;
        window.clearTimeout(this._announced);
        if (!words) return;
        this._announced = window.setTimeout(() => {
            if (!Game.gameStarted || Game.gameOver || Game.isTraining || this._sky !== sky) return;
            const { inventoryMsg } = UIManager.ui;
            inventoryMsg.text(words).fadeOut(0).fadeIn(250);
            clearTimeout(Game.inventoryMsgTimeout);
            Game.inventoryMsgTimeout = window.setTimeout(() => inventoryMsg.fadeOut(250), 6000);
        }, 1800);
    }

    /**
     * After the look's palette: the snow's colours over it (the map, the minimap, the background), or the colours put
     * back as they were after a snowy match
     */
    applyColors(): void {
        // (the buildings' ground decorations too: a patch of sand, a road)
        MapManager.snowGround = this._sky === "snow";
        const colors = Game.colors as unknown as Record<string, Color>;
        if (this._sky === "snow") {
            this._stock ??= new Map(Object.entries(colors).map(([key, color]) => [key, new Color(color)]));
            for (const [key, value] of Object.entries(SNOW_PALETTE)) colors[key] = new Color(value);
            colors.background = new Color(SNOW_PALETTE.grass);
            // (a ghillie suit takes the ground's colour: white in the snow)
            colors.ghillie = new Color(SNOW_PALETTE.grass);
        } else if (this._stock) {
            for (const [key, color] of this._stock) colors[key] = color;
            this._stock = undefined;
        } else return;
        Game.pixi.renderer.background.color = colors.background;
        MapManager.gasRender?.recolor(Game.colors.gas);
        Game.gasRender?.recolor(Game.colors.gas);
    }

    /** leaving a match: no sky, no sounds */
    /**
     * no more lightning this match: leaving it, while the menu fades in over its last frames (the sky's reset comes
     * after: a strike in those moments rolled its thunder over the menu, 1.06.1's second review)
     */
    quiet(): void {
        this._nextStrike = Number.POSITIVE_INFINITY;
    }

    reset(): void {
        this._sky = "clear";
        this._strike = undefined;
        window.clearTimeout(this._announced);
        NightLight.reset();
        WarAudio.setRain(0);
        WarAudio.setWind(0);
        if (this._layer) this._layer.visible = false;
    }

    private _build(): Container {
        const layer = this._layer = new Container();
        layer.zIndex = SKY_ZINDEX;
        layer.eventMode = "none";
        this._tint = new Sprite(Texture.WHITE);
        this._tint.blendMode = "multiply";
        this._dusk = new Sprite(duskLightTexture());
        this._dusk.blendMode = "multiply";
        this._sun = new Sprite(sunTexture());
        this._sun.anchor.set(0.5);
        this._sun.blendMode = "add";
        this._sun.tint = 0xff8a3a;
        // (0.98.6: the sun's rays at dusk, with the lighting at High)
        this._rays = new Sprite(sunRaysTexture());
        this._rays.anchor.set(0.5);
        this._rays.blendMode = "add";
        this._rays.tint = 0xffb060;
        this._flash = new Sprite(Texture.WHITE);
        this._flash.tint = 0xe9efff;
        layer.addChild(this._tint, this._dusk, this._sun, this._rays, this._flash);
        return layer;
    }

    /** every frame, after the camera: the tint, the sun's glow, the lightning */
    update(): void {
        const sky = this._sky;
        if (!Game.gameStarted || sky === "clear" || sky === "night") {
            if (this._layer) this._layer.visible = false;
            return;
        }
        const layer = this._layer && !this._layer.destroyed ? this._layer : this._build();
        if (layer.parent !== CameraManager.container) CameraManager.addObject(layer);
        layer.visible = true;
        const { _tint: tint, _dusk: dusk, _sun: sun, _rays: rays, _flash: flash } = this;
        if (!tint || !dusk || !sun || !rays || !flash) return;

        // over the whole screen (in the camera container, where the screen's corner is now)
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const { width, height } = Game.pixi.screen;
        const left = -offset.x / scale, top = -offset.y / scale;
        const w = width / scale, h = height / scale;

        // the colours, where the grade doesn't give them
        const colours = TINTS[sky];
        tint.visible = colours !== undefined && !WarLook.shaders;
        if (tint.visible && colours) {
            tint.position.set(left, top);
            tint.width = w;
            tint.height = h;
            tint.tint = colours.color;
            tint.alpha = colours.alpha;
        }

        // dusk: the light falling off away from the low sun (at every level: the grade alone kept it a warm day), and
        // the sun's own warm glow from the lower left
        dusk.visible = sun.visible = sky === "dusk";
        if (sun.visible) {
            dusk.position.set(left, top);
            dusk.width = w;
            dusk.height = h;
            // (with the shaders, the grade warms and darkens it too: the falloff at about half its strength there)
            dusk.alpha = WarLook.shaders ? 0.55 : 1;
            const size = Math.max(w, h) * 1.7;
            sun.position.set(left - w * 0.12, top + h * 1.08);
            sun.width = sun.height = size;
            sun.alpha = 0.3;
        }
        // the sun's rays (0.98.6, the lighting at High): fanning out from the low sun, turning slowly
        rays.visible = sun.visible && Lighting.rich;
        if (rays.visible) {
            rays.position.copyFrom(sun.position);
            rays.width = rays.height = Math.max(w, h) * 2.6;
            rays.rotation = performance.now() * 0.001 * RAYS.turn;
            rays.alpha = RAYS.alpha;
        }

        // a thunderstorm: lightning now and then, its thunder after (later the farther it struck)
        const now = performance.now();
        if (sky === "storm") {
            // (none under the end screen; watching on after your death, the storm goes on: 1.06.1's audit; till the
            // match's end reaches the screen, its card up: 1.06.1's second review)
            if (!this._strike && now >= this._nextStrike && !(Game.gameOver && !Game.spectating) && !Game.matchEnded) {
                const distance = 0.1 + Math.random() * 0.9;
                const peak = 0.2 + 0.42 * (1 - distance);
                // (from one side or the other, as its thunder is heard: the shadows fall away from it, 0.98.6)
                const pan = (Math.random() * 2 - 1) * 0.8;
                const from = Math.atan2(-(0.3 + Math.random() * 0.7), pan * 1.5);
                this._strike = {
                    start: now,
                    pulses: Math.random() < 0.6
                        ? [{ at: 0, peak, fade: 70 }, { at: 120, peak: peak * 0.45, fade: 60 }, { at: 210, peak: peak * 0.9, fade: 320 }]
                        : [{ at: 0, peak: peak * 0.8, fade: 90 }, { at: 150, peak, fade: 380 }],
                    from
                };
                this._nextStrike = now + 6000 + Math.random() * 12000;
                WarAudio.thunder(0.25 + distance * 3.4, 1 - distance * 0.85, pan);
            }
            const { alpha, over } = this._strikeNow(now);
            if (over) this._strike = undefined;
            flash.visible = alpha > 0.005;
            if (flash.visible) {
                flash.position.set(left, top);
                flash.width = w;
                flash.height = h;
                flash.alpha = alpha;
            }
        } else flash.visible = false;
    }

    /** the strike's flash now (0 none): the brightest of its pulses at `now`, and whether it's over */
    private _strikeNow(now: number): { readonly alpha: number, readonly over: boolean } {
        const strike = this._strike;
        if (!strike) return { alpha: 0, over: true };
        const t = now - strike.start;
        let alpha = 0;
        let over = true;
        for (const pulse of strike.pulses) {
            const age = t - pulse.at;
            if (age < 0) {
                over = false;
                continue;
            }
            // (up in 15 ms, then fading)
            const k = age < 15 ? age / 15 : Math.exp(-(age - 15) / pulse.fade);
            if (k > 0.01) over = false;
            alpha = Math.max(alpha, pulse.peak * k);
        }
        return { alpha, over };
    }

    /**
     * the lightning as it flashes now (0.98.6, the lighting: its shadows fall away from the strike while it flashes,
     * the water glints with it): how bright, and the way the shadows go; nothing between strikes
     */
    lightning(): Lightning | undefined {
        const strike = this._strike;
        if (!strike || this._sky !== "storm") return undefined;
        const { alpha } = this._strikeNow(performance.now());
        if (alpha <= 0.005) return undefined;
        return { alpha: Math.min(1, alpha / 0.62), angle: strike.from + Math.PI };
    }

    /**
     * the colour things up in the air are drawn in (the jump-in's planes and chutes, `night` at night): they're over the
     * camera's container, where the sky's tint, the dusk's falling light and the lightning are, and none of it fell on
     * them, bright over a grey-blue storm (1.06.1's audit). The sky's tint where the grade isn't drawn, as the world's;
     * at dusk its light at the screen's middle, as strong as the world's; a lightning flash lifts it toward white
     */
    airTint(night: number): number {
        const sky = this._sky;
        if (sky === "night") return night;
        let r = 1, g = 1, b = 1;
        const multiply = (color: number, alpha: number): void => {
            r *= 1 - alpha + alpha * ((color >> 16) & 0xff) / 255;
            g *= 1 - alpha + alpha * ((color >> 8) & 0xff) / 255;
            b *= 1 - alpha + alpha * (color & 0xff) / 255;
        };
        const colours = TINTS[sky];
        if (colours && !WarLook.shaders) multiply(colours.color, colours.alpha);
        if (sky === "dusk") multiply(0xdecdd2, WarLook.shaders ? 0.55 : 1);
        const flash = this._flash?.visible ? this._flash.alpha : 0;
        if (flash > 0) {
            r += (1 - r) * flash;
            g += (1 - g) * flash;
            b += (1 - b) * flash;
        }
        const byte = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 255)));
        return (byte(r) << 16) | (byte(g) << 8) | byte(b);
    }
}

export const WarSky = new WarSkyClass();
